// Bell icon in the TopBar with a dropdown showing the last N in-app notifications.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Trash2, ShoppingBag, Wallet, Info, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNotificationCenter } from "@/hooks/useNotificationCenter";

const KIND_META = {
  info:    { Icon: Info,         bg: "bg-blue-50",   color: "text-blue-600" },
  order:   { Icon: ShoppingBag,  bg: "bg-blue-50", color: "text-primary-brand" },
  wallet:  { Icon: Wallet,       bg: "bg-green-50",  color: "text-green-600" },
  success: { Icon: Check,        bg: "bg-green-50",  color: "text-green-600" },
  warning: { Icon: AlertCircle,  bg: "bg-yellow-50", color: "text-yellow-700" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "الآن";
  if (diff < 3600) return `منذ ${Math.floor(diff / 60)} د`;
  if (diff < 86400) return `منذ ${Math.floor(diff / 3600)} س`;
  try { return d.toLocaleDateString("ar", { dateStyle: "short" }); } catch { return ""; }
}

export default function NotificationBell() {
  const { user } = useAuth();
  const { items, unread, markAllRead, markRead, clearAll } = useNotificationCenter(user?.id);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (!open) return;
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!user) return null;

  const top = items.slice(0, 10);

  return (
    <div className="relative" ref={boxRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-xl hover:bg-[var(--surface-alt)] transition-colors"
        aria-label="الإشعارات"
        data-testid="notif-bell-btn"
      >
        <Bell size={18} className="text-[var(--text-primary)]"/>
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary-brand text-white text-[10px] font-black flex items-center justify-center ring-2 ring-white"
            data-testid="notif-unread-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-2 w-[340px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl talabya-shadow-float border border-[var(--border)] overflow-hidden z-50 animate-fade-up"
          data-testid="notif-panel"
        >
          <div className="flex items-center justify-between p-3 border-b border-[var(--border)] bg-[var(--surface-alt)]">
            <div className="flex items-center gap-2">
              <Bell size={16} className="text-primary-brand"/>
              <span className="font-black text-sm" style={{fontFamily:"Cairo"}}>الإشعارات</span>
              {unread > 0 && (
                <span className="text-[10px] bg-primary-brand text-white font-black px-1.5 py-0.5 rounded-full">{unread} جديد</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[11px] font-bold text-primary-brand hover:bg-blue-50 px-2 py-1 rounded-lg"
                  data-testid="notif-mark-all-read"
                >
                  <Check size={12} className="inline"/> الكل مقروء
                </button>
              )}
              {items.length > 0 && (
                <button
                  onClick={clearAll}
                  className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"
                  aria-label="مسح الكل"
                  data-testid="notif-clear-all"
                >
                  <Trash2 size={13}/>
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[400px] overflow-y-auto" data-testid="notif-list">
            {top.length === 0 ? (
              <div className="py-10 text-center text-sm text-[var(--text-secondary)]">
                <Bell size={28} className="mx-auto mb-2 opacity-40"/>
                لا توجد إشعارات بعد
              </div>
            ) : (
              <ul className="divide-y divide-[var(--border)]">
                {top.map((n) => {
                  const meta = KIND_META[n.kind] || KIND_META.info;
                  const Icon = meta.Icon;
                  const Wrap = n.url ? Link : "div";
                  const wrapProps = n.url ? { to: n.url, onClick: () => { markRead(n.id); setOpen(false); } } : {};
                  return (
                    <li key={n.id}>
                      <Wrap
                        {...wrapProps}
                        onClickCapture={() => markRead(n.id)}
                        className={`flex items-start gap-3 p-3 hover:bg-[var(--surface-alt)] transition-colors ${!n.read ? "bg-blue-50/40" : ""}`}
                        data-testid={`notif-item-${n.id}`}
                      >
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${meta.bg}`}>
                          <Icon size={15} className={meta.color}/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-black text-[13px] text-[var(--text-primary)] truncate">{n.title}</span>
                            {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-primary-brand shrink-0"/>}
                          </div>
                          {n.body && <div className="text-[11px] text-[var(--text-secondary)] mt-0.5 line-clamp-2">{n.body}</div>}
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] text-[var(--text-secondary)]">{timeAgo(n.at)}</span>
                            {n.orderCode && (
                              <span className="text-[10px] font-mono font-bold bg-[var(--surface-alt)] px-1.5 py-0.5 rounded">
                                {n.orderCode}
                              </span>
                            )}
                          </div>
                        </div>
                      </Wrap>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
